import React, { useState } from 'react';
import { CheckCircle, XCircle, Clock, User } from 'lucide-react';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { EmptyState } from '../../../components/common/EmptyState';
import { Button } from '../../../components/common/Button';
import { RejectModal } from './RejectModal';
import type { HolidayRequest, HolidayRequestStatus } from '../../../utils/types';

interface AdminHolidayTableProps {
  requests: HolidayRequest[];
  isLoading?: boolean;
  onApprove: (id: string, notes?: string) => void;
  onReject: (id: string, reason: string) => void;
  onToggleStatus: (id: string, reason?: string) => void;
  isProcessing?: Record<string, boolean>;
}

const statusConfig: Record<HolidayRequestStatus, { label: string; className: string }> = {
  pending: {
    label: 'Pending',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  approved: {
    label: 'Approved',
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-800 border-red-200',
  },
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const AdminHolidayTable: React.FC<AdminHolidayTableProps> = ({
  requests,
  isLoading,
  onApprove,
  onReject,
  onToggleStatus,
  isProcessing = {},
}) => {
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  const handleRejectClick = (id: string) => {
    setSelectedRequestId(id);
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = (reason: string) => {
    if (selectedRequestId) {
      onReject(selectedRequestId, reason);
      setRejectModalOpen(false);
      setSelectedRequestId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <EmptyState
        title="No holiday requests"
        description="There are no holiday requests to manage."
        icon={<Clock className="w-12 h-12 text-gray-400" />}
      />
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                From
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                To
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Days
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reason
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Requested
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests.map((request) => {
              const StatusConfig = statusConfig[request.status];
              const isPending = request.status === 'pending';
              const processing = isProcessing[request.id];

              return (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {request.user_details?.first_name} {request.user_details?.last_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {request.user_details?.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{formatDate(request.start_date)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{formatDate(request.end_date)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {request.total_days} {request.is_half_day && '(Half)'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-900 truncate max-w-xs block" title={request.reason}>
                      {request.reason}
                    </span>
                    {request.rejection_reason && (
                      <span className="text-xs text-red-600 block mt-1" title={request.rejection_reason}>
                        Reason: {request.rejection_reason}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${StatusConfig.className}`}>
                      {StatusConfig.label}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">
                      {formatDateTime(request.requested_at)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {isPending ? (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="success"
                          icon={<CheckCircle className="w-4 h-4" />}
                          onClick={() => onApprove(request.id)}
                          isLoading={processing}
                          disabled={processing}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<XCircle className="w-4 h-4" />}
                          onClick={() => handleRejectClick(request.id)}
                          isLoading={processing}
                          disabled={processing}
                        >
                          Reject
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <span className="text-sm text-gray-500">
                          {request.approved_by_username ? (
                            <>By {request.approved_by_username}</>
                          ) : (
                            '-'
                          )}
                        </span>
                        {/* Toggle Status Button for Approved/Rejected */}
                        {request.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="danger"
                            icon={<XCircle className="w-4 h-4" />}
                            onClick={() => onToggleStatus(request.id, 'Changed to rejected by admin')}
                            isLoading={processing}
                            disabled={processing}
                          >
                            Reject
                          </Button>
                        )}
                        {request.status === 'rejected' && (
                          <Button
                            size="sm"
                            variant="success"
                            icon={<CheckCircle className="w-4 h-4" />}
                            onClick={() => onToggleStatus(request.id, 'Changed to approved by admin')}
                            isLoading={processing}
                            disabled={processing}
                          >
                            Approve
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Reject Modal */}
      <RejectModal
        isOpen={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedRequestId(null);
        }}
        onConfirm={handleRejectConfirm}
        isLoading={selectedRequestId ? isProcessing[selectedRequestId] : false}
      />
    </>
  );
};

export default AdminHolidayTable;
